<template>
  <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-5 tw-border tw-border-slate-100">
    <button
      class="tw-w-full tw-text-xs tw-text-slate-600 hover:tw-text-slate-900 tw-flex tw-items-center tw-justify-start tw-mb-3"
      @click="$emit('back')"
    >
      <svg
        class="tw-w-4 tw-h-4 tw-mr-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
      Back to quote overview
    </button>

    <h3 class="tw-text-base tw-font-semibold tw-text-slate-900 tw-mb-1">
      Step 2 — Your details &amp; payment
    </h3>
    <p class="tw-text-xs tw-text-slate-500">
      Complete your billing and delivery details to finish your payment.
    </p>

    <!-- Delivery & Collections Guidelines -->
    <div class="tw-mt-4">
      <div
        class="tw-rounded-lg tw-border tw-p-4 tw-transition-colors"
        :class="deliveryAccepted ? 'tw-border-emerald-200 tw-bg-emerald-50' : 'tw-border-slate-200 tw-bg-slate-50'"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-gap-3">
          <div class="tw-flex tw-items-center tw-gap-3">
            <div
              class="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full"
              :class="deliveryAccepted ? 'tw-bg-emerald-100' : 'tw-bg-slate-200'"
            >
              <svg
                v-if="deliveryAccepted"
                class="tw-h-4 tw-w-4 tw-text-emerald-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <svg
                v-else
                class="tw-h-4 tw-w-4 tw-text-slate-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
                />
              </svg>
            </div>
            <div>
              <p
                class="tw-text-sm tw-font-medium"
                :class="deliveryAccepted ? 'tw-text-emerald-800' : 'tw-text-slate-700'"
              >
                {{ deliveryAccepted ? 'Delivery & Collections Instructions accepted' : 'Delivery & Collections Instructions' }}
              </p>
              <p
                v-if="!deliveryAccepted"
                class="tw-text-xs tw-text-slate-500"
              >
                Please read and accept before continuing
              </p>
            </div>
          </div>
          <button
            type="button"
            class="tw-inline-flex tw-items-center tw-rounded-md tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-shadow-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 tw-transition-colors"
            :class="deliveryAccepted 
              ? 'tw-border tw-border-slate-200 tw-bg-white tw-text-slate-700 hover:tw-bg-slate-50 focus:tw-ring-slate-500' 
              : 'tw-border tw-border-transparent tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 focus:tw-ring-blue-500'"
            @click="$emit('open-delivery')"
          >
            {{ deliveryAccepted ? 'Review' : 'Accept Instructions' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delivery Guide Video -->
    <div class="tw-mt-3">
      <button
        type="button"
        class="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-700 tw-shadow-sm hover:tw-bg-slate-50 hover:tw-border-slate-300 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 tw-transition-colors"
        @click="showVideoModal = true"
      >
        <span class="tw-inline-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-600 tw-text-white">
          <svg
            class="tw-h-3 tw-w-3"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        Watch Delivery Guide
      </button>
    </div>

    <DeliveryVideoModal
      :show="showVideoModal"
      :video-id="deliveryVideoId"
      @close="showVideoModal = false"
    />

    <!-- Delivery Vehicle Information & Notes -->
    <div class="tw-mt-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-4">
      <h4 class="tw-text-sm tw-font-semibold tw-text-slate-900 tw-mb-3">
Delivery Requirements
</h4>
      
      <div class="tw-space-y-3">
        <label
class="tw-flex tw-items-start tw-cursor-pointer tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-transition-colors"
               :class="vehicleSizeAccepted ? 'tw-border-emerald-200 tw-bg-emerald-50' : 'tw-border-slate-200 tw-bg-slate-50 hover:tw-bg-slate-100'"
>
          <input
type="checkbox"
                 :checked="vehicleSizeAccepted"
                 class="tw-mt-0.5 tw-h-4 tw-w-4 tw-text-emerald-600 tw-border-slate-300 tw-rounded focus:tw-ring-emerald-500"
                 @change="$emit('update:vehicleSizeAccepted', $event.target.checked)"
>
          <div class="tw-text-sm tw-text-slate-700">
            <p>
              I understand that all our deliveries are on
              <span class="tw-relative tw-inline-block tw-group tw-align-middle">
                <a
                  :href="vehicleImageUrl"
                  target="_blank"
                  class="tw-font-semibold tw-text-blue-600 hover:tw-underline tw-inline-flex tw-items-center"
                >18 tonne vehicles <i class="pi pi-external-link tw-ml-1 tw-text-[10px]" /></a>
                <span class="tw-pointer-events-none tw-absolute tw-left-0 tw-top-full tw-z-50 tw-mt-2 tw-hidden group-hover:tw-block tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-2 tw-shadow-xl">
                  <img
                    :src="vehicleImageUrl"
                    alt="18 tonne vehicle preview"
                    class="tw-block tw-w-64 tw-max-w-none tw-h-auto tw-rounded"
                  >
                </span>
              </span>
              (equivalent to the size of a double decker bus).
            </p>
          </div>
        </label>

        <label
class="tw-flex tw-items-start tw-cursor-pointer tw-gap-3 tw-p-3 tw-rounded-lg tw-border tw-transition-colors"
               :class="kerbsideAccepted ? 'tw-border-emerald-200 tw-bg-emerald-50' : 'tw-border-slate-200 tw-bg-slate-50 hover:tw-bg-slate-100'"
>
          <input
type="checkbox"
                 :checked="kerbsideAccepted"
                 class="tw-mt-0.5 tw-h-4 tw-w-4 tw-text-emerald-600 tw-border-slate-300 tw-rounded focus:tw-ring-emerald-500"
                 @change="$emit('update:kerbsideAccepted', $event.target.checked)"
>
          <div class="tw-text-sm tw-text-slate-700">
            <p>
              I accept that all deliveries are kerbside and will ensure two suitably able people are on-site to accept delivery.
            </p>
          </div>
        </label>
      </div>

      <div class="tw-mt-4">
        <label
          for="delivery_notes"
          class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
        >
          Delivery Notes (optional)
        </label>
        <textarea
          id="delivery_notes"
          :value="notes"
          name="delivery_notes"
          rows="3"
          placeholder="E.g close to schools, bridges, on a London red route, restricted access, parking restrictions, etc."
          class="tw-w-full tw-border tw-border-slate-300 tw-p-2 tw-rounded tw-text-sm tw-resize-y focus:tw-border-blue-500 focus:tw-ring-1 focus:tw-ring-blue-500 focus:tw-outline-none"
          @input="$emit('update:notes', $event.target.value)"
        />
      </div>
    </div>

    <div
      class="tw-mt-4 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4"
    >
      <p class="tw-text-xs tw-font-medium tw-text-slate-700 tw-uppercase tw-tracking-wide">
        Payment option
      </p>
      <div class="tw-mt-3 tw-space-y-2">
        <label class="tw-flex tw-items-start tw-cursor-pointer tw-gap-3">
          <input
            :checked="paymentType === 'super'"
            type="radio"
            value="super"
            class="tw-mt-1 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 focus:tw-ring-blue-500"
            @change="$emit('update:paymentType', 'super')"
          >
          <span class="tw-text-sm tw-text-slate-700">
            <span class="tw-font-medium tw-text-slate-900">Pay now</span>
            <span class="tw-text-slate-500"> (card payment via Super Payments)</span>
          </span>
        </label>

        <label class="tw-flex tw-items-start tw-cursor-pointer tw-gap-3">
          <input
            :checked="paymentType === 'bacs'"
            type="radio"
            value="bacs"
            class="tw-mt-1 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 focus:tw-ring-blue-500"
            @change="$emit('update:paymentType', 'bacs')"
          >
          <span class="tw-text-sm tw-text-slate-700">
            <span class="tw-font-medium tw-text-slate-900">BACS or Direct bank transfer</span>
          </span>
        </label>

        <label
          v-if="creditAvailable"
          class="tw-flex tw-items-start tw-cursor-pointer tw-gap-3"
        >
          <input
            :checked="paymentType === 'account'"
            type="radio"
            value="account"
            class="tw-mt-1 tw-h-4 tw-w-4 tw-text-blue-600 tw-border-slate-300 focus:tw-ring-blue-500"
            @change="$emit('update:paymentType', 'account')"
          >
          <span class="tw-text-sm tw-text-slate-700">
            <span class="tw-font-medium tw-text-slate-900">Add to account</span>
            <span class="tw-text-slate-500"> (pending acceptance)</span>
          </span>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import DeliveryVideoModal from './DeliveryVideoModal.vue'

export default {
  name: 'PaymentMethodStep',
  components: {
    DeliveryVideoModal
  },
  props: {
    creditAvailable: {
      type: Boolean,
      default: false
    },
    paymentType: {
      type: String,
      default: 'super'
    },
    deliveryAccepted: {
      type: Boolean,
      default: false
    },
    kerbsideAccepted: {
      type: Boolean,
      default: false
    },
    vehicleSizeAccepted: {
      type: Boolean,
      default: false
    },
    notes: {
      type: String,
      default: ''
    }
  },
  emits: [
    'back',
    'update:paymentType',
    'open-delivery',
    'update:kerbsideAccepted',
    'update:vehicleSizeAccepted',
    'update:notes'
  ],
  data() {
    return {
      showVideoModal: false
    }
  },
  computed: {
    vehicleImageUrl() {
      return (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.vehicleImageUrl)
        ? backendConfig.pageData.vehicleImageUrl
        : 'https://s3.eu-west-3.wasabisys.com/qcksld/2019%20new%20vehicle.jpg' // Use a sensible default or placeholder
    },
    deliveryVideoId() {
      return (typeof backendConfig !== 'undefined' && backendConfig?.pageData?.deliveryVideoId)
        ? backendConfig.pageData.deliveryVideoId
        : 'ZRZXQreE224'
    }
  }
}
</script>
